import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppHeader from '../AppHeader';
import { useAuth } from '../../contexts/AuthContext';
import { deleteCampaign, listCampaigns, COMMUNICATION_CATEGORIES } from '../../services/communications';
import EmergencySendDialog from './EmergencySendDialog';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'previewed', label: 'Previewed' },
  { value: 'sending', label: 'Sending' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_COLORS = {
  draft: 'default',
  previewed: 'info',
  sending: 'info',
  sent: 'success',
  failed: 'error',
  cancelled: 'error',
};

export default function CommunicationsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const readOnly = user?.role === 'viewer';

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilters, setCategoryFilters] = useState([]);
  const [statusFilters, setStatusFilters] = useState([]);
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [emergencyToast, setEmergencyToast] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page + 1,
        page_size: pageSize,
      };
      if (categoryFilters.length) params.categories = categoryFilters.map((c) => c.value).join(',');
      if (statusFilters.length) params.statuses = statusFilters.map((s) => s.value).join(',');
      if (createdAfter) params.created_after = createdAfter;
      if (createdBefore) params.created_before = createdBefore;
      const { results, count: c } = await listCampaigns(params);
      setRows(results);
      setCount(c);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, categoryFilters, statusFilters, createdAfter, createdBefore]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get('emergency') === '1' && isAdmin) {
      setEmergencyOpen(true);
    }
  }, [searchParams, isAdmin]);

  const closeEmergency = () => {
    setEmergencyOpen(false);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('emergency');
        return next;
      },
      { replace: true }
    );
  };

  const catLabel = (v) => COMMUNICATION_CATEGORIES.find((c) => c.value === v)?.label || v;
  const statusLabel = (v) => STATUS_OPTIONS.find((s) => s.value === v)?.label || v;

  const openDeleteDialog = (e, row) => {
    e.stopPropagation();
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteCampaign(deleteTarget.id);
      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setCount((c) => Math.max(0, c - 1));
      setDeleteOpen(false);
      setDeleteTarget(null);
    } catch (e) {
      const msg =
        (typeof e.response?.data?.detail === 'string' && e.response.data.detail) ||
        (Array.isArray(e.response?.data?.detail) && e.response.data.detail.map((x) => x?.msg || x).join('; ')) ||
        e.message ||
        'Delete failed';
      setError(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <AppHeader backTo="/" />
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Campaigns
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isAdmin && (
              <Button variant="outlined" color="error" onClick={() => setEmergencyOpen(true)}>
                Emergency broadcast
              </Button>
            )}
            {!readOnly && (
              <Button variant="contained" onClick={() => navigate('/communications/new')}>
                New campaign
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Autocomplete
              multiple
              size="small"
              sx={{ minWidth: 220, flex: 1 }}
              options={COMMUNICATION_CATEGORIES}
              getOptionLabel={(o) => o.label}
              value={categoryFilters}
              onChange={(_, v) => {
                setCategoryFilters(v);
                setPage(0);
              }}
              renderInput={(params) => <TextField {...params} label="Categories" />}
            />
            <Autocomplete
              multiple
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
              options={STATUS_OPTIONS}
              getOptionLabel={(o) => o.label}
              value={statusFilters}
              onChange={(_, v) => {
                setStatusFilters(v);
                setPage(0);
              }}
              renderInput={(params) => <TextField {...params} label="Statuses" />}
            />
            <TextField
              size="small"
              type="date"
              label="Created after"
              InputLabelProps={{ shrink: true }}
              value={createdAfter}
              onChange={(e) => {
                setCreatedAfter(e.target.value);
                setPage(0);
              }}
            />
            <TextField
              size="small"
              type="date"
              label="Created before"
              InputLabelProps={{ shrink: true }}
              value={createdBefore}
              onChange={(e) => {
                setCreatedBefore(e.target.value);
                setPage(0);
              }}
            />
            <Button variant="outlined" onClick={load} disabled={loading}>
              Refresh
            </Button>
          </Box>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {count} campaign(s)
        </Typography>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Created by</TableCell>
                {isAdmin && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 7 : 6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 7 : 6}>
                    <Typography variant="body2" color="text.secondary">
                      No campaigns yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/communications/${r.id}`)}
                  >
                    <TableCell>{r.title || '—'}</TableCell>
                    <TableCell>{catLabel(r.category)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabel(r.status)}
                        color={STATUS_COLORS[r.status] || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{r.audience_summary || r.audience_type}</TableCell>
                    <TableCell>{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</TableCell>
                    <TableCell>{r.created_by_name || r.created_by_email || '—'}</TableCell>
                    {isAdmin && (
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip
                          title={r.status === 'sending' ? 'Cannot delete while sending' : 'Delete campaign'}
                        >
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Delete campaign"
                              disabled={r.status === 'sending'}
                              onClick={(e) => openDeleteDialog(e, r)}
                              color="error"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </TableContainer>
      </Container>

      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
        <DialogTitle>Delete campaign?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete campaign &apos;{deleteTarget?.title || `#${deleteTarget?.id}`}&apos;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <EmergencySendDialog
        open={emergencyOpen}
        onClose={closeEmergency}
        onSent={(data) => {
          const n = data?.recipients ?? data?.preview_recipient_count;
          setEmergencyToast(
            n != null ? `Emergency message sent to ${n} recipients.` : 'Emergency message sent.'
          );
          load();
        }}
      />
      <Snackbar
        open={Boolean(emergencyToast)}
        autoHideDuration={6000}
        onClose={() => setEmergencyToast('')}
        message={emergencyToast}
      />
    </Box>
  );
}
